import React, { memo } from 'react';
import { cn } from '../../lib/utils';

/**
 * ArtifactPanelSwitcher — which artifact the right-hand surface is showing.
 *
 * Only one panel can hold the surface at a time, which used to mean the others
 * were simply gone: a turn that wrote a spec *and* re-planned the board opened
 * the editor, and the only way back to the changeset was to scroll the
 * transcript until you found that turn's chip. This is the way back.
 *
 * Renders nothing at all when there is only one artifact to show, so the common
 * case — one document, no tasks — looks exactly as it did before.
 *
 * Availability is decided by the caller and derived during render from what
 * exists (a file is loaded, the board has tasks), never tracked in state: a
 * switcher that remembers a panel whose content has gone offers a dead segment.
 */

const SPRING_CLASS = 'transition-all duration-300 ease-out';

function ArtifactPanelSwitcherImpl({ panels = [], active, onSelect }) {
    // One artifact is not a choice.
    if (panels.length < 2) return null;

    const activeIndex = Math.max(0, panels.findIndex((p) => p.id === active));

    return (
        <div className="flex-shrink-0 border-b border-white/10 bg-black/20 px-3 py-2">
            <div
                role="tablist"
                aria-label="Artifact panel"
                className="relative flex items-center gap-1 rounded-xl bg-secondary/30 p-1"
            >
                {/* The indicator is a single element sliding under the labels,
                    not a per-segment background: two backgrounds cross-fading
                    reads as a blink at this size, and one moving object reads as
                    the same panel changing what it holds. */}
                <div
                    aria-hidden
                    className={cn('absolute h-[calc(100%-8px)] rounded-lg bg-primary shadow-lg', SPRING_CLASS)}
                    style={{
                        width: `calc(${100 / panels.length}% - 4px)`,
                        left: `calc(${activeIndex * (100 / panels.length)}% + 4px)`,
                    }}
                />

                {panels.map((panel) => {
                    const Icon = panel.icon;
                    const isActive = panel.id === active;
                    return (
                        <button
                            key={panel.id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            title={panel.title || panel.label}
                            data-testid={`panel-switch-${panel.id}`}
                            onClick={() => onSelect(panel.id)}
                            className={cn(
                                'relative z-10 flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5',
                                'font-mono text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-200',
                                isActive ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {Icon ? <Icon className="h-3.5 w-3.5 flex-shrink-0" /> : null}
                            <span className="truncate">{panel.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export const ArtifactPanelSwitcher = memo(ArtifactPanelSwitcherImpl);
export default ArtifactPanelSwitcher;
